import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { CURRENCIES } from '@/constants';
import { getMinMaxDataCurrentText } from '@/utils';
// import { formatCompactParts } from '@/utils';

export const createStrictField = ({
  labelId,
  value,
  isFormated = true,
  formatMessage,
  excludedKeys = [],
}) => {
  if (value === null || value === undefined || value === '') return null;

  const isObjectTypeValue = typeof value === 'object';
  const requiredKeys = isObjectTypeValue ? Object.keys(value) : [];

  if (!requiredKeys.length && isObjectTypeValue) return null;

  const hasAllRequired = isObjectTypeValue
    ? requiredKeys
        .filter((key) => !excludedKeys.includes(key))
        .every((key) => value[key] !== null && value[key] !== undefined)
    : true;

  if (!isFormated && value) {
    return {
      label: formatMessage({ id: labelId }),
      value,
    };
  }

  return hasAllRequired
    ? {
        label: formatMessage({ id: labelId }),
        value: isObjectTypeValue
          ? formatMessage({ id: `${labelId}.value` }, value)
          : formatMessage({ id: `${labelId}.value.${value}` }),
      }
    : null;
};

const compact = (array) => array.filter(Boolean);
const formatListWithIntl = ({ items, idPrefix, formatMessage }) =>
  items?.length
    ? items.map((key) => formatMessage({ id: `${idPrefix}.${key}` })).join(', ')
    : null;

const getLoanRate = ({ formatMessage, rateDisplay, loanRateYearComment }) => ({
  label: formatMessage({
    id: 'details.basicConditions.loanRateYear.listProducts',
  }),
  value: rateDisplay?.list ? (
    <>
      <ul>
        {rateDisplay?.list.map((item) => (
          <li key={item?.text}>
            {item?.text} {item?.value}
            <br />
            {item?.subline}
          </li>
        ))}
      </ul>

      {loanRateYearComment && <p>{loanRateYearComment}</p>}
    </>
  ) : (
    <>
      <p>{rateDisplay?.rangeText}</p>
      <p>{rateDisplay?.tooltip}</p>
      {loanRateYearComment && <p>{loanRateYearComment}</p>}
    </>
  ),
});

export function getCommissionFields({
  oneTimeCommission,
  oneTimeCommissionPercent,
  oneTimeCommissionAmount,
  oneTimeCommissionComment,
  monthlyCommission,
  monthlyCommissionPercent,
  monthlyCommissionAmount,
  monthlyCommissionComment,
  currency,
  formatMessage,
}) {
  const fields = [];

  const buildValue = (percent, amount) => {
    if (percent !== null && amount !== null) {
      return formatMessage(
        { id: 'details.basicConditions.commission.value.percentAndAmount' },
        { percent, amount, currency }
      );
    }

    if (percent !== null) {
      return formatMessage(
        { id: 'details.basicConditions.commission.value.percent' },
        { percent }
      );
    }

    if (amount !== null) {
      return formatMessage(
        { id: 'details.basicConditions.commission.value.amount' },
        { amount, currency }
      );
    }

    return null;
  };

  if (oneTimeCommission) {
    const value = buildValue(oneTimeCommissionPercent, oneTimeCommissionAmount);

    if (value) {
      fields.push({
        label: formatMessage({
          id: 'details.basicConditions.commission.oneTime.label',
        }),
        value,
        comment: oneTimeCommissionComment || null,
      });
    }
  }

  if (monthlyCommission) {
    const value = buildValue(monthlyCommissionPercent, monthlyCommissionAmount);

    if (value) {
      fields.push({
        label: formatMessage({
          id: 'details.basicConditions.commission.monthly.label',
        }),
        value,
        comment: monthlyCommissionComment || null,
      });
    }
  }

  return fields;
}

export const useBusinessConditionsFields = (data) => {
  if (!data)
    return {
      navs: null,
      fields: null,
    };

  const {
    loanLimitNumber = {},
    loanLimitNumberComment = '',
    currency = 'UAH',
    termMonths = {},
    loanPurpose = [],
    provisionForm = '',
    repayment = '',
    loanRateYear = null,
    effectiveInterestRate = {},
    interestRate = {},
    isPrepayment = false,
    prepaymentDescription = '',
    commission = null,
    commissionPercent = null,
    commissionDescription = '',
    isDownPayment = false,
    downPayment = null,
    loanRecipient = [],
    businessSize = [],
    minimalBusinessRegistrationPeriod = null,
    minimalBusinessRegistrationPeriodComment = '',
    revenue = {},
    activityScope = [],
    isLoanSecurity = false,
    loanSecurity = [],
    isLoanSurety = false,
    loanSuretyAmount = null,
    requestConsiderationTermDays = {},
    onlineSubmission = false,
    onlineCabinet = false,
    personalAssistance = false,
    supportProgram = false,
    aboutSupportProgram = '',
    additionalRequirements = '',
    rateDisplay = null,
    loanRateYearComment = '',
    loanSecurityComment = '',
    effectiveInterestRateComment = '',
    downPaymentComment = '',
    oneTimeCommission = false,
    oneTimeCommissionPercent = null,
    oneTimeCommissionAmount = null,
    oneTimeCommissionComment = '',
    monthlyCommission = false,
    monthlyCommissionPercent = null,
    monthlyCommissionAmount = null,
    monthlyCommissionComment = '',
    ownershipForm,
    decisionActiveTerm,
    paymentChannel,
    loanLimitMaxFirst,
    requiredDocument,
  } = data;
  const { locale, formatMessage } = useIntl();

  return useMemo(() => {
    const сommissionFields = getCommissionFields({
      oneTimeCommission,
      oneTimeCommissionPercent,
      oneTimeCommissionAmount,
      oneTimeCommissionComment,
      monthlyCommission,
      monthlyCommissionPercent,
      monthlyCommissionAmount,
      monthlyCommissionComment,
      currency,
      formatMessage,
    });

    const loanLimitNumberText = getMinMaxDataCurrentText({
      key: 'details.basicConditions.loanLimitNumber',
      data: loanLimitNumber,
      formatMessage,
      withRange: true,
      locale,
      shortNumbers: false,
    });

    const fields = {
      basicConditions: compact([
        {
          label: formatMessage({
            id: 'details.basicConditions.loanLimitNumber',
          }),
          value: loanLimitNumberText ? (
            <>
              <p>
                {loanLimitNumberText} {CURRENCIES?.[currency] || null}
              </p>
              {loanLimitNumberComment && <p>{loanLimitNumberComment}</p>}
            </>
          ) : null,
        },
        createStrictField({
          labelId: 'details.basicConditions.currency',
          value: currency?.toUpperCase(),
          isFormated: false,
          formatMessage,
        }),
        createStrictField({
          labelId: 'details.basicConditions.termMonths',
          value: {
            min: termMonths?.min,
            max: termMonths?.max,
          },
          formatMessage,
        }),
        createStrictField({
          labelId: 'details.basicConditions.loanPurpose',
          value: formatListWithIntl({
            items: loanPurpose,
            idPrefix: 'details.basicConditions.loanPurpose.value',
            formatMessage,
          }),
          isFormated: false,
          formatMessage,
        }),
        createStrictField({
          labelId: 'details.basicConditions.provisionForm',
          value: provisionForm,
          formatMessage,
        }),
        createStrictField({
          labelId: 'details.basicConditions.repayment',
          value: repayment,
          formatMessage,
        }),
        {
          ...getLoanRate({ formatMessage, rateDisplay, loanRateYearComment }),
        },
        createStrictField({
          labelId: 'details.basicConditions.effectiveInterestRate',
          value: {
            min: effectiveInterestRate?.min,
            max: effectiveInterestRate?.max,
            comment: effectiveInterestRateComment,
          },
          formatMessage,
          excludedKeys: ['comment'],
        }),
        createStrictField({
          labelId: 'details.basicConditions.interestRate',
          value: interestRate,
          formatMessage,
        }),
        {
          label: formatMessage({ id: 'details.basicConditions.isPrepayment' }),
          value: (
            <>
              <p>
                {isPrepayment
                  ? formatMessage({ id: 'common.yes' })
                  : formatMessage({ id: 'common.no' })}
              </p>
              {prepaymentDescription && <p>{prepaymentDescription}</p>}
            </>
          ),
        },
        createStrictField({
          labelId: 'details.basicConditions.commission',
          value: {
            commission: commission
              ? formatMessage({
                  id: `details.basicConditions.commission.${commission}`,
                })
              : null,
            commissionPercent,
            commissionDescription,
          },
          formatMessage,
        }),
        {
          label: formatMessage({ id: 'details.basicConditions.downPayment' }),
          value:
            isDownPayment && downPayment ? (
              <>
                <p>{downPayment} %</p>
                <p>{downPaymentComment}</p>
              </>
            ) : (
              formatMessage({ id: 'common.no' })
            ),
          formatMessage,
        },
        ...(сommissionFields?.length
          ? [
              {
                label: formatMessage({
                  id: 'details.basicConditions.commission',
                }),
                value: (
                  <ul>
                    {сommissionFields.map((field, index) => {
                      if (field?.label && field?.value) {
                        return (
                          <li key={index}>
                            {field?.label} {field?.value}
                            {field?.comment && <p>{field?.comment}</p>}
                          </li>
                        );
                      }

                      return null;
                    })}
                  </ul>
                ),
              },
            ]
          : []),
        createStrictField({
          labelId:
            'details.borrowerRequirements.additional.requestConsiderationTerm',
          value: {
            min: requestConsiderationTermDays?.min,
            max: requestConsiderationTermDays?.max,
          },
          formatMessage,
        }),
        createStrictField({
          labelId:
            'details.borrowerRequirements.basicConditions.decisionActiveTerm',
          value: {
            days: decisionActiveTerm,
          },
          formatMessage,
        }),
        createStrictField({
          labelId:
            'details.borrowerRequirements.basicConditions.loanLimitMaxFirst',
          value: {
            limit: loanLimitMaxFirst,
            currency: CURRENCIES?.[currency],
          },
          formatMessage,
        }),
        createStrictField({
          labelId: 'details.basicConditions.paymentChannel',
          value: formatListWithIntl({
            items: paymentChannel,
            idPrefix: 'details.basicConditions.paymentChannel.value',
            formatMessage,
          }),
          isFormated: false,
          formatMessage,
        }),
      ]),
      borrowerRequirements: compact([
        createStrictField({
          labelId: 'details.borrowerRequirements.loanRecipient',
          value: formatListWithIntl({
            items: loanRecipient,
            idPrefix: 'details.borrowerRequirements.loanRecipient.value',
            formatMessage,
          }),
          isFormated: false,
          formatMessage,
        }),
        createStrictField({
          labelId: 'details.borrowerRequirements.requiredDocument',
          value: formatListWithIntl({
            items: requiredDocument,
            idPrefix: 'details.borrowerRequirements.requiredDocument.value',
            formatMessage,
          }),
          isFormated: false,
          formatMessage,
        }),
        createStrictField({
          labelId: 'details.borrowerRequirements.ownershipForm',
          value: formatListWithIntl({
            items: ownershipForm,
            idPrefix: 'details.borrowerRequirements.ownershipForm.value',
            formatMessage,
          }),
          isFormated: false,
          formatMessage,
        }),
        createStrictField({
          labelId: 'details.borrowerRequirements.businessSize',
          value: formatListWithIntl({
            items: businessSize,
            idPrefix: 'details.borrowerRequirements.businessSize.value',
            formatMessage,
          }),
          isFormated: false,
          formatMessage,
        }),
        createStrictField({
          labelId:
            'details.borrowerRequirements.minimalBusinessRegistrationPeriod',
          value: minimalBusinessRegistrationPeriod ? (
            <>
              <p>
                {minimalBusinessRegistrationPeriod}{' '}
                {formatMessage({ id: 'details.borrowerRequirements.months' })}
              </p>

              {minimalBusinessRegistrationPeriodComment && (
                <p>{minimalBusinessRegistrationPeriodComment}</p>
              )}
            </>
          ) : null,
          isFormated: false,
          formatMessage,
        }),
        createStrictField({
          labelId: 'details.borrowerRequirements.revenue',
          value: {
            min: revenue?.min,
            max: revenue?.max,
            period: revenue?.period
              ? formatMessage({
                  id: `details.borrowerRequirements.revenue.period.${revenue?.period}`,
                })
              : null,
          },
          formatMessage,
        }),
        createStrictField({
          labelId: 'details.borrowerRequirements.activityScope',
          value: formatListWithIntl({
            items: activityScope,
            idPrefix: 'details.borrowerRequirements.activityScope.value',
            formatMessage,
          }),
          isFormated: false,
          formatMessage,
        }),
      ]),
      collateral: compact([
        createStrictField({
          labelId: 'details.borrowerRequirements.collateral',
          value: {
            isLoanSecurity: isLoanSecurity
              ? formatMessage({
                  id: `details.borrowerRequirements.boolean.${isLoanSecurity ? 'yes' : 'no'}`,
                })
              : null,
            collateral: formatListWithIntl({
              items: loanSecurity,
              idPrefix: 'details.borrowerRequirements.collateral.type',
              formatMessage,
            }),
            comment: loanSecurityComment,
          },
          excludedKeys: ['collateral'],
          formatMessage,
        }),
        createStrictField({
          labelId: 'details.borrowerRequirements.loanSurety',
          value: {
            isSurety: formatMessage({
              id: `details.borrowerRequirements.boolean.${isLoanSurety ? 'yes' : 'no'}`,
            }),
            amount: loanSuretyAmount
              ? formatMessage(
                  {
                    id: 'details.borrowerRequirements.loanSurety.amount',
                  },
                  { amount: loanSuretyAmount }
                )
              : '',
            isAmount: !!loanSuretyAmount,
            currency: CURRENCIES?.[currency],
          },
          formatMessage,
          excludedKeys: ['amount'],
        }),
      ]),
      additional: compact([
        {
          label: formatMessage({
            id: 'details.borrowerRequirements.additional.onlineSubmission',
          }),
          value: formatMessage({
            id: `details.borrowerRequirements.boolean.${onlineSubmission ? 'yes' : 'no'}`,
          }),
        },
        {
          label: formatMessage({
            id: 'details.borrowerRequirements.additional.onlineCabinet',
          }),
          value: formatMessage({
            id: `details.borrowerRequirements.boolean.${onlineCabinet ? 'yes' : 'no'}`,
          }),
        },
        {
          label: formatMessage({
            id: 'details.borrowerRequirements.additional.personalAssistance',
          }),
          value: formatMessage({
            id: `details.borrowerRequirements.boolean.${personalAssistance ? 'yes' : 'no'}`,
          }),
        },
        createStrictField({
          labelId: 'details.borrowerRequirements.additional.supportProgram',
          value: `${formatListWithIntl({
            items: supportProgram,
            idPrefix:
              'details.borrowerRequirements.additional.supportProgram.value',
            formatMessage,
          })}${aboutSupportProgram ? `. ${aboutSupportProgram}` : ''}`,
          isFormated: false,
          formatMessage,
        }),
        createStrictField({
          labelId:
            'details.borrowerRequirements.additional.additionalRequirements',
          value: additionalRequirements,
          isFormated: false,
          formatMessage,
        }),
      ]),
    };

    return {
      navs: Object.keys(fields),
      fields,
    };
  }, [
    formatMessage,
    loanLimitNumber,
    loanLimitNumberComment,
    currency,
    termMonths,
    loanPurpose,
    provisionForm,
    repayment,
    loanRateYear,
    effectiveInterestRate,
    interestRate,
    isPrepayment,
    prepaymentDescription,
    commission,
    commissionPercent,
    commissionDescription,
    isDownPayment,
    downPayment,
    loanRecipient,
    businessSize,
    minimalBusinessRegistrationPeriod,
    revenue,
    activityScope,
    isLoanSecurity,
    loanSecurity,
    isLoanSurety,
    loanSuretyAmount,
    requestConsiderationTermDays,
    onlineSubmission,
    onlineCabinet,
    personalAssistance,
    supportProgram,
    aboutSupportProgram,
    additionalRequirements,
  ]);
};

export const useBusinessConditionsFieldsItem = (data) => {
  if (!data) return [];

  const {
    loanLimitNumber = {},
    loanLimitNumberComment = '',
    currency = 'uah',
    termMonths = {},
    loanPurpose = [],
    provisionForm = '',
    repayment = '',
    loanRateYear = null,
    effectiveInterestRate = {},
    interestRate = {},
    isPrepayment = false,
    prepaymentDescription = '',
    commission = null,
    commissionPercent = null,
    commissionDescription = '',
    isDownPayment = false,
    downPayment = null,
    loanRecipient = [],
    businessSize = [],
    minimalBusinessRegistrationPeriod = null,
    revenue = {},
    activityScope = [],
    isLoanSecurity = false,
    loanSecurity = [],
    isLoanSurety = false,
    loanSuretyAmount = null,
    requestConsiderationTermDays = {},
    onlineSubmission = false,
    onlineCabinet = false,
    personalAssistance = false,
    supportProgram = false,
    aboutSupportProgram = '',
    additionalRequirements = '',
    rateDisplay,
    loanRateYearComment = '',
  } = data;
  const { locale, formatMessage } = useIntl();

  const loanLimitNumberText = getMinMaxDataCurrentText({
    key: 'details.basicConditions.loanLimitNumber',
    data: loanLimitNumber,
    formatMessage,
    withRange: true,
    locale,
    shortNumbers: false,
  });

  return useMemo(
    () =>
      compact([
        {
          label: formatMessage({
            id: 'details.basicConditions.loanLimitNumber',
          }),
          value: loanLimitNumberText ? (
            <>
              <p>
                {loanLimitNumberText} {CURRENCIES?.[currency] || null}
              </p>
              {loanLimitNumberComment && <p>{loanLimitNumberComment}</p>}
            </>
          ) : null,
        },
        {
          ...getLoanRate({ formatMessage, rateDisplay, loanRateYearComment }),
        },
        createStrictField({
          labelId: 'details.basicConditions.termMonths',
          value: {
            min: termMonths?.min,
            max: termMonths?.max,
          },
          formatMessage,
        }),
        createStrictField({
          labelId: 'details.basicConditions.repayment',
          value: repayment,
          formatMessage,
        }),
        createStrictField({
          labelId: 'details.basicConditions.loanPurpose',
          value: formatListWithIntl({
            items: loanPurpose,
            idPrefix: 'details.basicConditions.loanPurpose.value',
            formatMessage,
          }),
          isFormated: false,
          formatMessage,
        }),
      ]),
    [
      formatMessage,
      loanLimitNumber,
      loanLimitNumberComment,
      currency,
      termMonths,
      loanPurpose,
      provisionForm,
      repayment,
      loanRateYear,
      effectiveInterestRate,
      interestRate,
      isPrepayment,
      prepaymentDescription,
      commission,
      commissionPercent,
      commissionDescription,
      isDownPayment,
      downPayment,
      loanRecipient,
      businessSize,
      minimalBusinessRegistrationPeriod,
      revenue,
      activityScope,
      isLoanSecurity,
      loanSecurity,
      isLoanSurety,
      loanSuretyAmount,
      requestConsiderationTermDays,
      onlineSubmission,
      onlineCabinet,
      personalAssistance,
      supportProgram,
      aboutSupportProgram,
      additionalRequirements,
    ]
  );
};
